import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./ProfileCard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

// Constants for default avatars
const DEFAULT_AVATARS = {
  Male: "https://api.pickledepot.com.my/storage/v1/object/public/avatars//4a514d47-a08f-4813-a6b9-62c8648ec23a_1745941356698.png",
  Female:
    "https://api.pickledepot.com.my/storage/v1/object/public/avatars//4a514d47-a08f-4813-a6b9-62c8648ec23a_1745941336398.png",
  Undisclosed:
    "https://api.pickledepot.com.my/storage/v1/object/public/avatars//4a514d47-a08f-4813-a6b9-62c8648ec23a_1745941300871.png",
  Other:
    "https://api.pickledepot.com.my/storage/v1/object/public/avatars//4a514d47-a08f-4813-a6b9-62c8648ec23a_1745941300871.png", // Fallback avatar (same as Undisclosed for now)
};

// Notification Modal Component
const NotificationModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Notification</h2>
        <p>{message}</p>
        <button className="modal-close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} /> Got It
        </button>
      </div>
    </div>
  );
};

const ProfileCard = ({
  name,
  email,
  gender,
  date_of_birth,
  age,
  status,
  avatarUrl,
  isEditing,
  onEditToggle,
  onSave,
}) => {
  const [editedData, setEditedData] = useState({
    name,
    gender,
    date_of_birth,
    avatarUrl,
    avatarFile: null,
  });

  const [notification, setNotification] = useState({ message: "", show: false });

  // Normalize gender to match DEFAULT_AVATARS keys (e.g., "male" -> "Male")
  const normalizeGender = (genderValue) => {
    if (!genderValue) return "Undisclosed";
    return (
      genderValue.charAt(0).toUpperCase() + genderValue.slice(1).toLowerCase()
    );
  };

  const normalizedGender = normalizeGender(gender);

  const [imagePreview, setImagePreview] = useState(
    avatarUrl || DEFAULT_AVATARS[normalizedGender] || DEFAULT_AVATARS.Other
  );
  const previousPreviewRef = useRef(
    avatarUrl || DEFAULT_AVATARS[normalizedGender] || DEFAULT_AVATARS.Other
  );

  useEffect(() => {
    if (
      previousPreviewRef.current !==
        (avatarUrl ||
          DEFAULT_AVATARS[normalizedGender] ||
          DEFAULT_AVATARS.Other) &&
      previousPreviewRef.current !== imagePreview
    ) {
      URL.revokeObjectURL(previousPreviewRef.current);
    }

    setEditedData({
      name,
      gender: normalizedGender,
      date_of_birth,
      avatarUrl, // Preserve the original avatarUrl
      avatarFile: null,
    });

    const defaultAvatar =
      DEFAULT_AVATARS[normalizedGender] || DEFAULT_AVATARS.Other;
    setImagePreview(avatarUrl || defaultAvatar);
    previousPreviewRef.current = avatarUrl || defaultAvatar;

    return () => {
      if (imagePreview !== (avatarUrl || defaultAvatar)) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [name, normalizedGender, date_of_birth, avatarUrl]);

  // Update avatar preview dynamically based on gender (only if no custom avatar exists)
  useEffect(() => {
    if (isEditing && !editedData.avatarFile) {
      const normalizedEditedGender = normalizeGender(editedData.gender);
      const defaultAvatar =
        DEFAULT_AVATARS[normalizedEditedGender] || DEFAULT_AVATARS.Other;

      // Only set the default avatar if there is no original avatarUrl
      if (!avatarUrl) {
        setImagePreview(defaultAvatar);
        setEditedData((prev) => ({
          ...prev,
          avatarUrl: defaultAvatar,
        }));
        previousPreviewRef.current = defaultAvatar;
      } else {
        // If there's an original avatarUrl, keep the preview as is unless a new file is uploaded
        setImagePreview(avatarUrl);
        previousPreviewRef.current = avatarUrl;
      }
    }
  }, [editedData.gender, isEditing, avatarUrl]);

  const handleChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (
        imagePreview !==
        (avatarUrl ||
          DEFAULT_AVATARS[normalizedGender] ||
          DEFAULT_AVATARS.Other)
      ) {
        URL.revokeObjectURL(imagePreview);
      }

      const previewUrl = URL.createObjectURL(file);
      setEditedData((prev) => ({
        ...prev,
        avatarFile: file,
        avatarUrl: null, // Clear avatarUrl since a new file is uploaded
      }));
      setImagePreview(previewUrl);
      previousPreviewRef.current = previewUrl;
    }
  };

  const handleSave = async () => {
    const success = await onSave(editedData);
    if (success) {
      if (
        imagePreview !==
        (avatarUrl ||
          DEFAULT_AVATARS[normalizedGender] ||
          DEFAULT_AVATARS.Other)
      ) {
        URL.revokeObjectURL(imagePreview);
      }
      const normalizedEditedGender = normalizeGender(editedData.gender);
      const defaultAvatar =
        DEFAULT_AVATARS[normalizedEditedGender] || DEFAULT_AVATARS.Other;
      setImagePreview(editedData.avatarUrl || avatarUrl || defaultAvatar);
      previousPreviewRef.current =
        editedData.avatarUrl || avatarUrl || defaultAvatar;
    }
  };

  const handleCloseNotification = () => {
    setNotification({ message: "", show: false });
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const minDate = "1920-01-01";
    if (selectedDate < minDate) {
      setNotification({
        message: "Date of Birth cannot be earlier than January 1, 1920.",
        show: true,
      });
      return;
    }
    handleChange("date_of_birth", selectedDate);
  };

  return (
    <div className="profile-card">
      {isEditing ? (
        <>
          <div className="avatar-upload-wrapper">
            <img
              src={imagePreview}
              alt="Avatar Preview"
              className="profile-avatar"
              onError={(e) => {
                console.error(
                  "ProfileCard.js: Error loading image, using fallback."
                );
                e.target.src = DEFAULT_AVATARS.Other;
              }}
            />
            <label className="add-picture-button">
              +
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="avatar-file-input"
              />
            </label>
          </div>
        </>
      ) : (
        <img
          src={
            avatarUrl ||
            DEFAULT_AVATARS[normalizedGender] ||
            DEFAULT_AVATARS.Other
          }
          alt="Avatar"
          className="profile-avatar"
          onError={(e) => {
            console.error(
              "ProfileCard.js: Error loading image, using fallback."
            );
            e.target.src = DEFAULT_AVATARS.Other;
          }}
        />
      )}
      <div className="profile-details">
        {isEditing ? (
          <>
            <div className="profile-name-status">
              <input
                type="text"
                value={editedData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="profile-input profile-name-input"
                placeholder="Enter username"
              />
              <span className="profile-status">{status}</span>
            </div>
            <p className="profile-email">{email}</p>
            <div className="edit-dob-gender">
              <div className="edit-field">
                <label className="edit-label">Gender:</label>
                <select
                  value={editedData.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  className="profile-input profile-select"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Undisclosed">Undisclosed</option>
                </select>
              </div>
              <div className="edit-field">
                <label className="edit-label">Date of Birth:</label>
                <input
                  type="date"
                  value={editedData.date_of_birth}
                  onChange={handleDateChange}
                  className="profile-input profile-date-input"
                  min="1920-01-01"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
            <div className="profile-actions">
              <button onClick={handleSave} className="save-button">
                Save
              </button>
              <button onClick={onEditToggle} className="cancel-button">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="profile-name-status">
              <h2 className="profile-name">{name}</h2>
              <span className="profile-status">{status}</span>
            </div>
            <p className="profile-email">{email}</p>
            <div className="profile-info">
              <strong>Gender:</strong> {gender || "Not specified"} |{" "}
              <strong>Age:</strong> {age || "Not specified"}
            </div>
            {status && (
              <Link to="/membership-upgrade" className="upgrade-link">
                Upgrade Membership?
              </Link>
            )}
            <button onClick={onEditToggle} className="edit-button">
              Edit Profile
            </button>
          </>
        )}
      </div>
      <NotificationModal
        isOpen={notification.show}
        onClose={handleCloseNotification}
        message={notification.message}
      />
    </div>
  );
};

export default ProfileCard;